import {
  type RequisiteIssue,
  validateBik,
  validateCorrespondentAccount,
  validateInn,
  validateKpp,
  validateOgrn,
  validateSettlementAccount,
} from '@glory/kernel';

import { normalizeInn, normalizeText } from './normalize.ts';

/** Объект журнала, живых обновлений и чата: клиент. */
export const CLIENT_ENTITY = 'client';

/** Организация (ООО, АО…), индивидуальный предприниматель или физическое лицо. */
export const CLIENT_KINDS = ['legal', 'entrepreneur', 'person'] as const;
export type ClientKind = (typeof CLIENT_KINDS)[number];

/** Потенциальный (пока без выигранных сделок), клиент, в архиве. */
export const CLIENT_STATUSES = ['prospect', 'active', 'archived'] as const;
export type ClientStatus = (typeof CLIENT_STATUSES)[number];

/** Откуда пришёл клиент. */
export const CLIENT_SOURCES = ['site', 'email', 'referral', 'outbound', 'other'] as const;
export type ClientSource = (typeof CLIENT_SOURCES)[number];

/**
 * Канал документов клиента (подпроект 9): `link` — письмо со ссылкой на страницу подписания, `edo` — ящик ЭДО клиента.
 * Выбор запоминается: клиент выбрал ЭДО однажды — дальше документы уходят в его ящик.
 */
export const CLIENT_DOCFLOWS = ['link', 'edo'] as const;
export type ClientDocflow = (typeof CLIENT_DOCFLOWS)[number];

export const NAME_MAX = 200;
export const TEXT_MAX = 300;
export const ABOUT_MAX = 5_000;

/**
 * Реквизиты организации и ИП; у физлица их нет. Банк и основание полномочий руководителя («Устава», «доверенности
 * № …», в родительном падеже) нужны документам: заявке, счёту и акту (подпроект 4).
 */
export const REQUISITE_FIELDS = [
  'inn',
  'kpp',
  'ogrn',
  'legalName',
  'address',
  'directorName',
  'directorPost',
  'directorBasis',
  'bankBik',
  'bankName',
  'bankCorrespondentAccount',
  'bankAccount',
] as const;
export type RequisiteField = (typeof REQUISITE_FIELDS)[number];

const REQUISITES_OF: Readonly<Record<ClientKind, readonly RequisiteField[]>> = {
  legal: REQUISITE_FIELDS,
  // У ИП нет КПП и руководителя: договор подписывает он сам.
  entrepreneur: [
    'inn',
    'ogrn',
    'legalName',
    'address',
    'bankBik',
    'bankName',
    'bankCorrespondentAccount',
    'bankAccount',
  ],
  person: [],
};

/** Какие реквизиты бывают у клиента этого вида. */
export const requisitesOf = (kind: ClientKind): readonly RequisiteField[] => REQUISITES_OF[kind];

/** Реквизиты есть у организации и ИП. */
export const hasRequisites = (kind: ClientKind): boolean => kind !== 'person';

/**
 * Вид меняется с физлица на организацию или ИП и между организацией и ИП (ошиблись видом — поправили);
 * обратно в физлицо — нет: у человека без компании реквизитов быть не может.
 */
export const canChangeKind = (from: ClientKind, to: ClientKind): boolean => from !== to && to !== 'person';

/** Во что можно сменить вид: у физлица — организация или ИП, у организации — ИП, у ИП — организация. */
export const kindChoices = (from: ClientKind): ClientKind[] =>
  CLIENT_KINDS.filter((to) => canChangeKind(from, to));

/**
 * Какие заполненные реквизиты пропадут при смене вида: у нового вида их нет (КПП и руководитель у ИП).
 * Остальные сохраняются, а ИНН и ОГРН должны подойти новому виду — это проверяет `clientIssues`.
 */
export function requisitesLostOnChange(
  current: Readonly<Partial<Record<RequisiteField, string | null>>>,
  from: ClientKind,
  to: ClientKind,
): RequisiteField[] {
  const kept = requisitesOf(to);
  return requisitesOf(from).filter((field) => !kept.includes(field) && (current[field] ?? null) !== null);
}

/** Длина ИНН по виду: 10 цифр у организации, 12 у ИП. */
export const innLength = (kind: ClientKind): number => (kind === 'legal' ? 10 : 12);

export type Requisites = Readonly<Record<RequisiteField, string | null>>;

export const EMPTY_REQUISITES: Requisites = {
  inn: null,
  kpp: null,
  ogrn: null,
  legalName: null,
  address: null,
  directorName: null,
  directorPost: null,
  directorBasis: null,
  bankBik: null,
  bankName: null,
  bankCorrespondentAccount: null,
  bankAccount: null,
};

/** Данные клиента, как их вводят в форме. */
export interface ClientInput {
  readonly kind: ClientKind;
  readonly name: string;
  readonly requisites?: Partial<Record<RequisiteField, string | null>> | undefined;
  readonly source?: ClientSource | null | undefined;
  readonly sourceNote?: string | null | undefined;
  readonly referredByClientId?: string | null | undefined;
  readonly website?: string | null | undefined;
  readonly about?: string | null | undefined;
}

/** Данные клиента, как их храним: нормализованные, лишние для вида реквизиты убраны. */
export interface ClientData {
  readonly kind: ClientKind;
  readonly name: string;
  readonly requisites: Requisites;
  readonly source: ClientSource | null;
  readonly sourceNote: string | null;
  readonly referredByClientId: string | null;
  readonly website: string | null;
  readonly about: string | null;
}

/** ИНН правильный, но не той формы: 12 цифр у организации и наоборот. */
export type KindIssue = 'inn_not_individual' | 'inn_not_legal' | 'ogrn_not_individual' | 'ogrn_not_legal';

export type ClientField = 'name' | RequisiteField | 'source' | 'sourceNote' | 'website' | 'about' | 'contact';

export interface ClientIssue {
  readonly field: ClientField;
  /** `required` / `too_long` / `website_format` и ошибки реквизитов — `validation.*` в словаре. */
  readonly issue: RequisiteIssue | KindIssue | 'required' | 'too_long' | 'website_format';
}

/** Название без лишних пробелов; пустое — null. */
export const normalizeName = (name: string): string | null => normalizeText(name);

/** Номера банка — без пробелов, как их копируют из выписки. */
const BANK_NUMBERS: readonly RequisiteField[] = ['bankBik', 'bankCorrespondentAccount', 'bankAccount'];

/**
 * Как ввели → как храним: без лишних пробелов, реквизиты чужого для вида набора пропадают,
 * КПП — заглавными.
 */
export function normalizeClient(input: ClientInput): ClientData {
  const allowed = requisitesOf(input.kind);
  const raw = input.requisites ?? {};
  const requisite = (field: RequisiteField): string | null => {
    if (!allowed.includes(field)) return null;
    const value = raw[field] ?? null;
    if (field === 'inn' || field === 'ogrn') return normalizeInn(value);
    if (field === 'kpp') return normalizeInn(value)?.toUpperCase() ?? null;
    if (BANK_NUMBERS.includes(field)) return normalizeInn(value);
    return normalizeText(value);
  };
  return {
    kind: input.kind,
    name: normalizeName(input.name) ?? '',
    requisites: {
      inn: requisite('inn'),
      kpp: requisite('kpp'),
      ogrn: requisite('ogrn'),
      legalName: requisite('legalName'),
      address: requisite('address'),
      directorName: requisite('directorName'),
      directorPost: requisite('directorPost'),
      directorBasis: requisite('directorBasis'),
      bankBik: requisite('bankBik'),
      bankName: requisite('bankName'),
      bankCorrespondentAccount: requisite('bankCorrespondentAccount'),
      bankAccount: requisite('bankAccount'),
    },
    source: input.source ?? null,
    sourceNote: normalizeText(input.sourceNote),
    referredByClientId: input.referredByClientId ?? null,
    website: normalizeText(input.website),
    about: input.about?.trim() ? input.about.trim() : null,
  };
}

// Адрес сайта: с http(s):// или без, домен с точкой (в том числе кириллический), дальше любой путь.
const WEBSITE = /^(?:https?:\/\/)?[^\s/?#]+\.[^\s/?#]{2,}(?:[/?#]\S*)?$/iu;

/** Ссылка на сайт для перехода: без схемы дописывается `https://`. */
export function websiteHref(website: string): string {
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
}

/**
 * Что не так с данными (уже нормализованными). ИНН у организации и ИП не обязателен — его дозаполняют
 * в карточке; если введён, проверяется по контрольным суммам и длине вида, как КПП и ОГРН.
 */
export function clientIssues(data: ClientData): ClientIssue[] {
  const issues: ClientIssue[] = [];
  const add = (field: ClientField, issue: ClientIssue['issue']) => issues.push({ field, issue });
  if (data.name === '') add('name', 'required');
  else if (data.name.length > NAME_MAX) add('name', 'too_long');

  const { inn, kpp, ogrn, legalName, address, directorName, directorPost, directorBasis } = data.requisites;
  const { bankBik, bankName, bankCorrespondentAccount, bankAccount } = data.requisites;
  if (hasRequisites(data.kind)) {
    const legal = data.kind === 'legal';
    if (inn !== null) {
      const issue = validateInn(inn);
      if (issue) add('inn', issue);
      else if (inn.length !== innLength(data.kind))
        add('inn', legal ? 'inn_not_legal' : 'inn_not_individual');
    }
    if (kpp !== null) {
      const issue = validateKpp(kpp);
      if (issue) add('kpp', issue);
    }
    if (ogrn !== null) {
      const issue = validateOgrn(ogrn);
      if (issue) add('ogrn', issue);
      else if (ogrn.length !== (legal ? 13 : 15))
        add('ogrn', legal ? 'ogrn_not_legal' : 'ogrn_not_individual');
    }
    for (const [field, value] of [
      ['legalName', legalName],
      ['address', address],
      ['directorName', directorName],
      ['directorPost', directorPost],
      ['directorBasis', directorBasis],
      ['bankName', bankName],
    ] as const) {
      if (value !== null && value.length > NAME_MAX * 2) add(field, 'too_long');
    }
    // Счета сверяются с БИК по ключу; без верного БИК — только 20 цифр (как у реквизитов студии).
    if (bankBik !== null) {
      const issue = validateBik(bankBik);
      if (issue) add('bankBik', issue);
    }
    const bikValid = bankBik !== null && validateBik(bankBik) === null;
    const accountIssue = (account: string, check: typeof validateSettlementAccount) =>
      bikValid ? check(account, bankBik) : /^\d{20}$/.test(account) ? null : 'account_format';
    if (bankCorrespondentAccount !== null) {
      const issue = accountIssue(bankCorrespondentAccount, validateCorrespondentAccount);
      if (issue) add('bankCorrespondentAccount', issue);
    }
    if (bankAccount !== null) {
      const issue = accountIssue(bankAccount, validateSettlementAccount);
      if (issue) add('bankAccount', issue);
    }
  }
  if (data.sourceNote !== null && data.sourceNote.length > TEXT_MAX) add('sourceNote', 'too_long');
  if (data.website !== null) {
    if (data.website.length > TEXT_MAX) add('website', 'too_long');
    else if (!WEBSITE.test(data.website)) add('website', 'website_format');
  }
  if (data.about !== null && data.about.length > ABOUT_MAX) add('about', 'too_long');
  return issues;
}

/** Порядок статусов в списке и фильтрах. */
export const STATUS_ORDER: readonly ClientStatus[] = ['prospect', 'active', 'archived'];

/** Клиент в архиве не получает новых лидов, пока его не вернули. */
export const isArchived = (status: ClientStatus): boolean => status === 'archived';

/** Куда вернётся клиент из архива: туда, откуда ушёл; неизвестно — потенциальный. */
export const statusAfterRestore = (before: ClientStatus | null): Exclude<ClientStatus, 'archived'> =>
  before === 'active' ? 'active' : 'prospect';
